import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import FlashAlert from "../../Components/FlashAlert/FlashAlert";
import apiService from "../../services/api.service";
import "./dashboardPolls.css";

const DashboardPolls = () => {
  const [polls, setPolls] = useState([]);
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [eventId, setEventId] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(1);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [options, setOptions] = useState(["", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [busyPollId, setBusyPollId] = useState(null);

  const eventNames = useMemo(
    () => Object.fromEntries(events.map((event) => [String(event.id), event.name])),
    [events]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pollsRes, eventsRes] = await Promise.all([
        apiService.getPolls(),
        apiService.getEvents(),
      ]);
      setPolls(pollsRes.data || []);
      setEvents(eventsRes.data || []);
      setError("");
    } catch {
      setError("Impossibile caricare i sondaggi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!polls.some((poll) => poll.is_active)) return undefined;
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [polls, load]);

  const updateOption = (index, value) => {
    setOptions((previous) => previous.map((option, i) => (i === index ? value : option)));
  };

  const addOption = () => setOptions((previous) => [...previous, ""]);

  const removeOption = (index) => {
    setOptions((previous) => previous.filter((_, i) => i !== index));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const cleanOptions = options.map((option) => option.trim()).filter(Boolean);
    if (!question.trim() || cleanOptions.length < 2 || !eventId) {
      setError("Seleziona un evento, inserisci una domanda e almeno due risposte.");
      return;
    }
    setSubmitting(true);
    try {
      await apiService.createPoll({
        question: question.trim(),
        event_id: Number(eventId),
        duration_seconds: Math.round(Number(durationMinutes) * 60),
        is_anonymous: isAnonymous,
        options: cleanOptions.map((label) => ({ label })),
      });
      setQuestion("");
      setOptions(["", ""]);
      setDurationMinutes(1);
      setIsAnonymous(true);
      setSuccess("Sondaggio creato. Puoi inviarlo in diretta quando vuoi.");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Creazione sondaggio non riuscita.");
    } finally {
      setSubmitting(false);
    }
  };

  const runPollAction = async (pollId, action, message) => {
    setBusyPollId(pollId);
    setError("");
    setSuccess("");
    try {
      await action(pollId);
      setSuccess(message);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Operazione non riuscita.");
    } finally {
      setBusyPollId(null);
    }
  };

  const showResults = async (pollId) => {
    setBusyPollId(pollId);
    setError("");
    try {
      const response = await apiService.getPollResults(pollId);
      setResults((previous) => ({ ...previous, [pollId]: response.data }));
    } catch (err) {
      setError(err.response?.data?.detail || "Impossibile caricare i risultati.");
    } finally {
      setBusyPollId(null);
    }
  };

  const downloadResults = async (pollId, format) => {
    setBusyPollId(pollId);
    setError("");
    try {
      await apiService.downloadPollResults(pollId, format);
    } catch (err) {
      setError(err.response?.data?.detail || "Download risultati non riuscito.");
    } finally {
      setBusyPollId(null);
    }
  };

  return (
    <div className="dashboard-card dashboard-polls">
      <h1 className="dashboard-card__title">Sondaggi</h1>
      <p className="dashboard-card__subtitle">
        Prepara una domanda a risposta multipla, scegli la durata e inviala agli utenti collegati alla diretta.
      </p>
      {error && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
      {success && <FlashAlert variant="success" onHidden={() => setSuccess("")}>{success}</FlashAlert>}

      <Form onSubmit={handleCreate} className="dashboard-polls__form">
        <Form.Group className="mb-3">
          <Form.Label>Evento</Form.Label>
          <Form.Select required value={eventId} onChange={(e) => setEventId(e.target.value)}>
            <option value="">Seleziona l&apos;evento</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>{event.name}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Domanda</Form.Label>
          <Form.Control
            required
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Es. Quanto ti è stata utile la sessione?"
          />
        </Form.Group>
        <Form.Group className="mb-3 dashboard-polls__duration">
          <Form.Label>Durata in diretta (minuti)</Form.Label>
          <Form.Control
            required
            type="number"
            min="1"
            max="1440"
            step="1"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Privacy del sondaggio</Form.Label>
          <div className="dashboard-polls__privacy-options">
            <Form.Check
              type="radio"
              id="poll-anonymous"
              name="poll-privacy"
              label="Anonimo"
              checked={isAnonymous}
              onChange={() => setIsAnonymous(true)}
            />
            <Form.Check
              type="radio"
              id="poll-public"
              name="poll-privacy"
              label="Pubblico (l'export include le email dei votanti)"
              checked={!isAnonymous}
              onChange={() => setIsAnonymous(false)}
            />
          </div>
        </Form.Group>
        <Form.Label>Risposte</Form.Label>
        {options.map((option, index) => (
          <div className="dashboard-polls__option-row" key={index}>
            <Form.Control
              required={index < 2}
              value={option}
              onChange={(e) => updateOption(index, e.target.value)}
              placeholder={`Risposta ${index + 1}`}
            />
            {options.length > 2 && (
              <Button
                type="button"
                variant="outline-secondary"
                onClick={() => removeOption(index)}
                aria-label={`Rimuovi risposta ${index + 1}`}
              >
                Rimuovi
              </Button>
            )}
          </div>
        ))}
        <Button type="button" variant="link" onClick={addOption} className="px-0 mb-3">
          + Aggiungi risposta
        </Button>
        <div>
          <Button type="submit" className="users-edit-btn" disabled={submitting || events.length === 0}>
            {submitting ? "Salvataggio..." : "Crea sondaggio"}
          </Button>
        </div>
      </Form>

      <h2 className="dashboard-polls__section-title">Sondaggi creati</h2>
      {loading && <p>Caricamento...</p>}
      {!loading && events.length === 0 && (
        <Alert variant="info">Crea prima un evento per poter preparare un sondaggio.</Alert>
      )}
      {!loading && polls.length === 0 && events.length > 0 && (
        <Alert variant="info">Nessun sondaggio creato.</Alert>
      )}
      {!loading && polls.length > 0 && (
        <div className="dashboard-events-list">
          {polls.map((poll) => {
            const pollResults = results[poll.id];
            return (
              <div className="dashboard-event-item dashboard-polls__item" key={poll.id}>
                <div className="dashboard-polls__item-main">
                  <p className="dashboard-event-item__title">{poll.question}</p>
                  <p className="dashboard-event-item__meta">
                    {eventNames[String(poll.event_id)] || `Evento #${poll.event_id}`}
                    {` · ${Math.round(poll.duration_seconds / 60)} min`}
                    {poll.is_anonymous ? " · Anonimo" : " · Pubblico"}
                    {poll.is_active ? " · In diretta" : " · Chiuso"}
                  </p>
                  <p className="dashboard-event-item__meta">
                    {poll.options?.map((option) => option.label).join(" · ")}
                  </p>
                  {pollResults && (
                    <div className="dashboard-polls__results">
                      <p>{pollResults.total_responses} risposte totali</p>
                      {pollResults.options.map((option) => (
                        <div className="dashboard-polls__result" key={option.id}>
                          <div className="dashboard-polls__result-label">
                            <span>{option.label}</span>
                            <strong>{option.percentage}%</strong>
                          </div>
                          <div className="dashboard-polls__result-track">
                            <span style={{ width: `${option.percentage}%` }} />
                          </div>
                          <small>{option.responses} risposte</small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="dashboard-polls__actions">
                  {poll.is_active ? (
                    <Button
                      variant="outline-danger"
                      disabled={busyPollId === poll.id}
                      onClick={() => runPollAction(poll.id, apiService.closePoll.bind(apiService), "Sondaggio chiuso.")}
                    >
                      Chiudi ora
                    </Button>
                  ) : (
                    <Button
                      className="users-edit-btn"
                      disabled={busyPollId === poll.id}
                      onClick={() => runPollAction(poll.id, apiService.activatePoll.bind(apiService), "Sondaggio inviato in diretta.")}
                    >
                      Invia in live
                    </Button>
                  )}
                  <Button
                    variant="outline-primary"
                    disabled={busyPollId === poll.id}
                    onClick={() => showResults(poll.id)}
                  >
                    {pollResults ? "Aggiorna risultati" : "Vedi risultati"}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    disabled={busyPollId === poll.id}
                    onClick={() => downloadResults(poll.id, "xlsx")}
                  >
                    Scarica XLSX
                  </Button>
                  <Button
                    variant="outline-secondary"
                    disabled={busyPollId === poll.id}
                    onClick={() => downloadResults(poll.id, "pdf")}
                  >
                    Scarica PDF
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardPolls;
